//  types
import { i18nEaiDevices } from 'models/i18n';

// constants
import {
  CharacteristicsRk,
  DeviceInstallationStatus
} from 'utils/characteristic';
import { DeviceReservedCapacityGuardingCharacteristicsValidationArguments } from 'views/modules/Eai/Devices/DeviceDetail/Characteristics/Common/DeviceReservedCapacityGuardingUtils';

// ==============================|| I18N EN DEVICES ||============================== //

const enEaiDevices: i18nEaiDevices = {
  createDeviceWizard: {
    title: 'Create device',
    step01: 'Device data',
    step02: 'Delivery point',
    save: 'Create device'
  },
  input: {
    DeviceId: 'Device ID',
    DeviceName: 'Device name',
    DeviceRole: 'Device role',
    ProductionDeviceType: 'Device production type',
    DeviceDeliveryPoint: 'Delivery point',
    DeviceDescription: 'Device description',
    DeviceCharacteristicGroupName: 'Device characteristic group name',
    DeviceCharacteristicGroupDescription:
      'Device characteristic group description',
    DeliveryPointName: 'Delivery point name',
    GatewayOfDispatcherSystem: 'Gateway of dispatcher system',
    ServiceCode: 'Group purpose'
  },
  deviceList: {
    searchInput: 'Enter name',
    createEntityButton: 'Create device',
    columns: {
      deviceId: 'Device ID',
      name: 'Device name',
      deliveryPointName: 'Delivery point name',
      installationStatus: 'Installation status'
    },
    filter: {
      status: 'Status',
      deliveryPointName: 'Delivery point name'
    }
  },
  validation: {
    'DeviceName-required': 'Device name is mandatory',
    'DeviceRole-required': 'Device role is mandatory',
    'DeviceDeliveryPoint-required': 'Delivery point is mandatory',
    'ProductionDeviceType-required': 'Device production type is mandatory',
    'DeviceName-max': 'Maximum length exceeded',
    'DeviceCharacteristicGroupName-required': 'Device group name is mandatory',
    'DeviceCharacteristicGroupName-length':
      'Device group length must be max 256 chars'
  },
  deviceDetail: {
    deviceData: {
      title: 'Device data',
      basicInfo: 'Basic info',
      installationStatusSwitch: 'Installed',
      installationStatus: {
        [DeviceInstallationStatus.INSTALLED]: 'Install',
        [DeviceInstallationStatus.NOT_INSTALLED]: 'Uninstall'
      },
      cancelGDSAssignmentValue: 'Cancel assignment of GDS'
    },
    deviceCharacteristics: {
      title: 'Characteristics',
      criticalCharacteristicsTitle:
        '* Critical characteristic necessary for the functioning of an optimization model',
      nonCriticalCharacteristicsTitle:
        '** Non-critical characteristic. In case the characteristic is not filled, the device will be ignored by the optimization model',
      deviceTechnicalCharacteristics: {
        title: 'Technical parameters',
        input: {
          panel_type: 'Panel type',
          panel_count: 'Panel count',
          panel_area: 'Panel area',
          panel_efficiency: 'Panel efficiency',
          panel_degradation: 'Panel degradation',
          panel_degradation_y1: 'Panel degradation after 1. year',
          empirical_coefficient_u0: 'U0 - Empirical coefficient',
          empirical_coefficient_u1: 'U1 - Empirical coefficient',
          max_power_output: 'Max power output',
          tkmo: 'Temperature coefficient of max power output',
          converter_efficiency: 'Converter efficiency',
          cssc: 'Cross section power cable',
          feeder_losses: 'Feeder losses',
          stc_irradiation: 'STC - irradiation',
          stc_temperature: 'STC - temperature',
          notc_irradiation: 'NOCT - irradiation',
          notc_temperature: 'NOCT - temperature',
          latitude: 'Latitude',
          longitude: 'Longitude',
          elevation: 'Elevation',
          panel_azimuth: 'Panel azimuth',
          panel_tilt: 'Panel tilt',
          correction_coefficient: 'Correction coefficient',
          battery_type: 'Battery type',
          battery_capacity_available_percentage: 'Available battery capacity',
          rated_charge: 'Rated charge',
          rated_discharge: 'Rated discharge',
          max_charge: 'Max charge',
          max_discharge: 'Max discharge',
          energy_availability_bess:
            'Energy availability of BESS (100% nominal power output)',
          energy_availability_bess_50:
            'Energy availability of BESS (50% nominal power output)',
          usable_energy: 'Usable energy',
          charge_cycles: 'Charge cycles',
          bat_life_expectancy: 'Battery life expectancy',
          bat_efficiency: 'Battery efficiency',
          bat_degradation_level: 'Battery degradation level',
          soc: 'SoC - State of charge',
          min_soc_end_condition: 'Minimum SoC end condition',
          max_soc_end_condition: 'Maximum SoC end condition',
          min_soc: 'Minimum SoC',
          max_soc: 'Maximum SoC',
          temperature_range_from: 'Temperature range from',
          temperature_range_to: 'Temperature range to',
          charging_characteristic: 'Charging characteristic',
          discharge_characteristic: 'Discharching characteristic',
          pmax_consumption: 'Upper limit of available power - PMAX',
          pmin_consumption: 'Lower limit of available power - PMIN',
          min_power_change_duration: 'Minimum power change duration',
          poweroutput_change_type: 'Power output change type',
          consumption_change_unitcost: 'Consumption change unitcost',
          degree_of_consumption_power_change_in_stages:
            'Degree of consumption power change in stages',
          poweroutput_rate_of_change: 'Power output rate of change',
          min_poweroutput_rate_of_change: 'Minimum power output rate of change',
          max_poweroutput_rate_of_change: 'Maximum power output rate of change',
          degree_of_consumption_power_change:
            'Degree of consumption power change',
          chart_characteristic_capacity: 'Capacity',
          chart_characteristic_poweroutput: 'Power output'
        },
        validation: {
          'panel_count-number': 'Value must be positive integer',
          'charge_cycles-number': 'Value must be positive integer',
          'value-number-range': 'Value must be in interval from',
          'value-number': 'Value must be number',
          'latitude-number':
            'Latitude must be number in range -90 to 90 with an accuracy of 10 decimal places',
          'longitude-number':
            'Longitude must be number in range -180 to 180 with an accuracy of 10 decimal places',
          'elevation-number': 'Elevation must be a valid number',
          'charging_characteristic-value':
            'First (0%) and last power value (100%) of charging characteristic is required',
          'discharge_characteristic-value':
            'First (0%) and last power value (100%) of discharge characteristic is required',
          'value-required': 'Value is required',
          'pmax_consumption-required': 'PMAX consumption is required',
          'pmax_consumption-value':
            'PMAX consumption must be greater or equal to PMIN consumption',
          'pmin_consumption-required': 'PMIN consumption is required',
          'pmin_consumption-value':
            'PMIN consumption must be lower or equal to PMAX consumption',
          'poweroutput_change_type-required':
            'Poweroutput change type is required',
          'consumption_change_unitcost-required':
            'Consumption change unitcost is required',
          'min_soc_end_condition-value':
            'Minimum SoC end condition must be lower than maximum SoC end condition',
          'max_soc_end_condition-value':
            'Maximum SoC end condition must be greater than minimum SoC end condition',
          'min_soc-value': 'Minimum SoC must be lower than maximum SoC',
          'max_soc-value': 'Maximum SoC must be greater than minimum SoC',
          'min_poweroutput_rate_of_change-value':
            'Minimum power output rate of change must be lower than maximum',
          'max_poweroutput_rate_of_change-value':
            'Maximum power output rate of change must be greater than minimum'
        },
        charging_characteristic: {
          title: 'Charging characteristic',
          input: {
            batteryCapacity: 'Capacity',
            chargingPower: 'Power output'
          },
          validation: {
            'chargingPower-required': 'Charging power is required',
            'chargingPower-number':
              'Charging power must be positive number or 0',
            'batteryCapacity-required': 'Capacity is required'
          },
          batteryCapacity: 'Capacity [%]',
          chargingPower: 'Power output [kW]',
          actions: 'Actions'
        },
        discharge_characteristic: {
          title: 'Discharge characteristic',
          input: {
            batteryCapacity: 'Capacity',
            dischargePower: 'Power output'
          },
          validation: {
            'dischargePower-required': 'Discharge power is required',
            'dischargePower-number':
              'Discharge power must be negative number or 0',
            'batteryCapacity-required': 'Capacity is required'
          },
          batteryCapacity: 'Capacity  [%]',
          dischargePower: 'Power output [kW]',
          actions: 'Actions'
        },
        power_change_characteristic: {
          title: 'Power change',
          input: {
            consumptionPowerDegree: 'Stage',
            changeDuration: 'Time'
          },
          consumptionPowerDegree: 'Stage [kW]',
          changeDuration: 'Time [s]',
          actions: 'Actions'
        }
      },
      production_model: {
        title: 'Device production model'
      },
      deviceEconomicalCharacteristics: {
        title: 'Economical parameters',
        input: {
          capex: 'Costs (CAPEX)',
          opex: 'Operation and maintenance costs (OPEX)',
          electricity_levelized_cost: 'Levelized Cost of Electricity',
          system_estimated_lifetime: 'System estimated lifetime',
          annual_produced_energy: 'Annual produced energy',
          discount_factor: 'Discount factor',
          storage_discounted_levelized_cost:
            'Discounted levelized cost of storage'
        },
        validation: {
          'capex-number': '',
          'opex-number': ''
        }
      },
      deviceWeatherCharacteristics: {
        title: 'Weather data',
        externalWeatherMonitoringTooltip:
          'By enabling this option, a Solcast license slot will be used for weather forecasting.',
        input: {
          external_weather_monitoring: 'External weather monitoring',
          solcast_resource_id: 'Solcast Resource ID',
          latitude: 'Latitude',
          longitude: 'Longitude'
        },
        validation: {
          'latitude-number':
            'Latitude must be number in range -90 to 90 with an accuracy of 10 decimal places',
          'longitude-number':
            'Longitude must be number in range -180 to 180 with an accuracy of 10 decimal places',
          'solcast_resource_id-match':
            'Solcast resource ID must be a combination of letters and numbers in the format xxxx-xxxx-xxxx-xxxx'
        }
      },
      deviceReservedCapacityGuardingCharacteristics: {
        title: 'RC guarding characteristics',
        input: {
          [CharacteristicsRk.reserved_capacity_guard_enabled]:
            'Enable guarding RC',
          [CharacteristicsRk.reserved_capacity_guard_priority]:
            'Priority of the guarding',
          [CharacteristicsRk.reserved_capacity_max_restriction_duration]:
            'Max restriction duration',
          [CharacteristicsRk.reserved_capacity_max_reduction_limit]:
            'Max change',
          [CharacteristicsRk.reserved_capacity_max_reduction_limit_type]:
            'Type of maximum power change during restriction',
          [CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction]:
            'Limit on the rate of change of power during restriction'
        },
        validation: {
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_REQUIRED]:
            'Priority of the guarding is required',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_NUMBER]:
            'Priority of the guarding must be a number',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_REQUIRED]:
            'Max restriction duration is required',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_NUMBER]:
            'Max restriction duration must be a number',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_REQUIRED]:
            'Max change is required',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_NUMBER]:
            'Max change must be a number',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_TYPE_REQUIRED]:
            'Type of maximum power change during restriction is required',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_REQUIRED]:
            'Limit on the rate of change of power during restriction is required',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_NUMBER]:
            'Limit on the rate of change of power during restriction must be a number'
        }
      },
      unitsOfMeasureLabels: {
        SQUARE_METRE: 'm²',
        SQUARE_MILIMETRE: 'mm²',
        PERCENT: '%',
        WATT_PEAK: 'Wp',
        EUR: '€',
        EUR_PER_KILOWATT_HOUR: '€/kWh',
        EUR_PER_MEGAWATT_HOUR: '€/MWh',
        DEGREE_CELSIA: '°C',
        WATT_PER_SQUARE_METRE: 'W/m²',
        DEGREE: '°',
        METRE: 'm',
        KWH: 'kWh',
        KW: 'kW',
        MW: 'MW',
        WATT: 'W',
        SQUARE_METRE_CUBED: '\u33A5',
        SECONDS: 's',
        EUR_PER_HOUR: '€/h',
        KW_PER_SECOND: 'kW/s',
        PERCENT_PER_DEGREE_CELSIA: '%/°C',
        YEARS: 'years'
      }
    },
    createDeviceCharacteristicGroupWizard: {
      title: 'Create device characteristic group',
      step01: 'Group data',
      step02: 'Group purpose',
      save: 'Create device characteristic group'
    }
  }
};

export default enEaiDevices;
